import { useCurrentFrame, useVideoConfig, spring, interpolate } from 'remotion';

// Types
import { AnimationPreset } from '@/types';

// Constants
import { ANIMATION_SPRING_CONFIGS, CLAMP_BOTH } from '@/constants';

export interface SlotMachineNumberProps {
  /** The final number value to display */
  value: number;
  /** Optional CSS class names */
  className?: string;
  /** Frame number when animation should start */
  startFrame: number;
  /** Duration of the spinning phase in frames (default: 20) */
  spinDuration?: number;
}

/**
 * SlotMachineNumber - Casino-style Number Spinner
 *
 * Animates a number with a slot machine spinning effect.
 * Features include:
 * - Rapid number changes during spin with blur
 * - Bouncy landing animation
 * - Glow pulse effect after landing
 * - Subtle rotation and jitter during spin
 */
export const SlotMachineNumber = ({
  value,
  className = '',
  startFrame,
  spinDuration = 20,
}: SlotMachineNumberProps): React.ReactNode => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const localFrame = frame - startFrame;

  // Phase 1: Spinning (rapid number changes with blur)
  const isSpinning = localFrame >= 0 && localFrame < spinDuration;

  // Phase 2: Landing with bounce
  const landingProgress = spring({
    frame: localFrame - spinDuration,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.SLOT_BOUNCE],
  });

  // Phase 3: Glow pulse after landing
  const glowProgress = spring({
    frame: localFrame - spinDuration - 10,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.GLOW],
  });

  // Display number during spin (rapidly changing)
  let displayNumber: number;

  if (localFrame < 0) {
    displayNumber = 0;
  } else if (isSpinning) {
    // Rapid counting effect
    const spinSpeed = Math.max(1, Math.floor((spinDuration - localFrame) / 3));
    displayNumber =
      Math.abs(
        Math.floor(Math.sin(localFrame * 2) * 10 + localFrame * spinSpeed),
      ) % 10;
  } else {
    displayNumber = value;
  }

  // Entry animation
  const entryProgress = spring({
    frame: localFrame,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.ELASTIC],
  });

  const entryOpacity = interpolate(entryProgress, [0, 0.2], [0, 1], CLAMP_BOTH);
  const entryScale = interpolate(entryProgress, [0, 1], [2.5, 1], CLAMP_BOTH);

  // Spinning blur effect
  const spinBlur = isSpinning
    ? interpolate(
        localFrame,
        [0, spinDuration * 0.7, spinDuration],
        [8, 4, 0],
        CLAMP_BOTH,
      )
    : 0;

  // Landing bounce scale
  const bounceScale = interpolate(
    landingProgress,
    [0, 0.4, 0.7, 1],
    [1, 1.3, 0.9, 1],
    CLAMP_BOTH,
  );

  // Glow effect
  const glowIntensity = interpolate(
    glowProgress,
    [0, 0.5, 1],
    [0, 1, 0.3],
    CLAMP_BOTH,
  );
  const glowScale = interpolate(
    glowProgress,
    [0, 0.3, 1],
    [1, 1.1, 1],
    CLAMP_BOTH,
  );

  // Rotation during spin
  const spinRotation = isSpinning
    ? interpolate(localFrame, [0, spinDuration], [0, -10], CLAMP_BOTH) +
      Math.sin(localFrame * 0.8) * 5
    : interpolate(landingProgress, [0, 0.5, 1], [-5, 3, 0], CLAMP_BOTH);

  // Vertical jitter during spin
  const jitterY = isSpinning ? Math.sin(localFrame * 3) * 3 : 0;

  const combinedScale = entryScale * bounceScale * glowScale;

  return (
    <span
      className={`${className} inline-block origin-center`}
      style={{
        opacity: entryOpacity,
        transform: `scale(${combinedScale}) rotate(${spinRotation}deg) translateY(${jitterY}px)`,
        filter: spinBlur > 0 ? `blur(${spinBlur}px)` : 'none',
        textShadow:
          glowIntensity > 0
            ? `0 0 ${20 * glowIntensity}px rgba(255, 200, 100, ${glowIntensity * 0.8}),
             0 0 ${40 * glowIntensity}px rgba(255, 150, 50, ${glowIntensity * 0.5}),
             0 0 ${60 * glowIntensity}px rgba(255, 100, 0, ${glowIntensity * 0.3})`
            : 'none',
      }}
    >
      {displayNumber}
    </span>
  );
};
